/**
 * S-51 助成金支払日登録（F-EXP-02）。kintone: 支払情報登録画面
 * 漁協は助成金支払予定日のみ入力可（支払日は参照のみ）。
 */
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import { CalendarCheck2, Eraser, HandCoins, Save } from 'lucide-react';
import {
  Button,
  Checkbox,
  DateField,
  EmptyState,
  ErrorState,
  FormField,
  Loading,
  PageHeader,
  Panel,
  RadioGroup,
  SearchPanel,
  TextField,
  useConfirm,
  useToast,
} from '@/components';
import { useFiscalYear } from '@/fiscal-year/FiscalYearProvider';
import { errorMessage } from '@/lib/api';
import { formatNumber } from '@/lib/format';
import { cx } from '@/lib/cx';
import { CellDate } from '../common/cells';
import { Money, Num, OperatorSelect, SummaryStrip } from '../common/components';
import { UNSAVED_MESSAGE, useUnsavedChangesGuard } from '../common/useUnsavedChangesGuard';
import { useOperatorScope } from '../common/useOperatorScope';
import { SUBSIDY_TARGETS, useSaveSubsidyPayments, useSubsidyPayments, type SubsidyFilter, type SubsidyRowDto } from './api';

type SearchForm = Required<Omit<SubsidyFilter, 'businessOperatorId'>> & { businessOperatorId: number | null };

const EMPTY: SearchForm = {
  businessOperatorId: null,
  target: '賃金・用船料',
  vesselName: '',
  registrationNo: '',
  departureFrom: '',
  departureTo: '',
  surveyFrom: '',
  surveyTo: '',
  scheduledFrom: '',
  scheduledTo: '',
  paidFrom: '',
  paidTo: '',
  scheduled: '両方',
  paid: '両方',
};

function useFilter(): [SearchForm, (f: SearchForm) => void] {
  const [params, setParams] = useSearchParams();
  const filter = useMemo<SearchForm>(() => {
    const op = params.get('businessOperatorId');
    return { ...EMPTY, ...Object.fromEntries([...params.entries()].filter(([k]) => k in EMPTY)), businessOperatorId: op ? Number(op) : null } as SearchForm;
  }, [params]);
  const set = (f: SearchForm) => {
    const next = new URLSearchParams();
    Object.entries(f).forEach(([k, v]) => v != null && v !== '' && next.set(k, String(v)));
    setParams(next, { replace: true });
  };
  return [filter, set];
}

const range = (a: string | undefined, b: string | undefined) => [a, b].some(Boolean);

export function SubsidyPaymentsPage() {
  const { year } = useFiscalYear();
  const toast = useToast();
  const confirm = useConfirm();
  const { isCoop, fixedOperatorId } = useOperatorScope();
  const [filter, setFilter] = useFilter();
  const operatorId = isCoop ? fixedOperatorId : filter.businessOperatorId;
  const query = useSubsidyPayments(year, { ...filter, businessOperatorId: operatorId }, operatorId != null);
  const save = useSaveSubsidyPayments();
  const { control, register, handleSubmit, reset } = useForm<SearchForm>({ values: { ...filter, businessOperatorId: operatorId } });
  const [rows, setRows] = useState<SubsidyRowDto[]>([]);
  const [bulk, setBulk] = useState<{ scheduled: boolean; paid: boolean; date: string }>({ scheduled: true, paid: false, date: '' });

  useEffect(() => setRows(query.data?.items ?? []), [query.data]);
  const original = useMemo(() => new Map((query.data?.items ?? []).map((r) => [r.id, `${r.subsidyScheduledOn}|${r.subsidyPaidOn}`])), [query.data]);
  const dirty = rows.filter((r) => original.get(r.id) !== `${r.subsidyScheduledOn}|${r.subsidyPaidOn}`);
  const isWage = filter.target === '賃金・用船料';
  // 登録せずに別画面（ホーム等）へ移動しようとしたら確認する（kintone 検証指摘）
  useUnsavedChangesGuard(dirty.length > 0);

  const onSearch = async (v: SearchForm) => {
    if (!isCoop && v.businessOperatorId == null) return toast.error('事業実施者は必須です');
    if (dirty.length && !(await confirm({ title: '確認', message: UNSAVED_MESSAGE, confirmLabel: 'OK' }))) return;
    setFilter(v);
  };

  const applyBulk = async (clear: boolean) => {
    if (!clear && !bulk.date) return toast.error('一括設定用の日付が入力されていません！');
    if (!bulk.scheduled && !bulk.paid) return toast.error('助成金支払予定日、助成金支払日が一つもチェックされていません！');
    if (!(await confirm({ title: '確認', message: clear ? '日付の一括クリア処理を開始しますか？' : '日付の一括設定処理を開始しますか？', confirmLabel: 'OK' }))) return;
    setRows((prev) =>
      prev.map((r) => ({
        ...r,
        subsidyScheduledOn: bulk.scheduled ? (clear ? null : (r.subsidyScheduledOn ?? bulk.date)) : r.subsidyScheduledOn,
        subsidyPaidOn: bulk.paid && !isCoop ? (clear ? null : (r.subsidyPaidOn ?? bulk.date)) : r.subsidyPaidOn,
      })),
    );
  };

  const onSubmit = async () => {
    if (!dirty.length) return toast.info('変更はありません');
    if (!(await confirm({ title: '登録の確認', message: '登録更新処理を開始しますか？', confirmLabel: 'OK' }))) return;
    try {
      const res = await save.mutateAsync({
        target: filter.target,
        items: dirty.map((r) => (isCoop ? { id: r.id, subsidyScheduledOn: r.subsidyScheduledOn } : { id: r.id, subsidyScheduledOn: r.subsidyScheduledOn, subsidyPaidOn: r.subsidyPaidOn })),
      });
      toast.success('登録更新しました。', `${formatNumber(res.updated)} 件`);
    } catch (e) {
      toast.error('保存できませんでした', errorMessage(e));
    }
  };

  const sum = (f: (r: SubsidyRowDto) => number | null) => rows.reduce((s, r) => s + (f(r) ?? 0), 0);

  return (
    <>
      <PageHeader
        eyebrow="経費・支払"
        title="助成金支払日登録"
        description="調査日ごとの賃金・用船料、速報ごとの燃油・潤滑油・現像代について、助成金支払予定日・支払日を一括で登録します。"
        meta={dirty.length ? <span className="bs-badge bs-badge--warning">未登録の変更 {dirty.length} 件</span> : undefined}
        actions={
          <Button variant="primary" icon={<Save size={16} />} onClick={() => void onSubmit()} loading={save.isPending} disabled={!rows.length}>
            登録
          </Button>
        }
      />

      <SearchPanel onSubmit={handleSubmit(onSearch)} onReset={() => reset({ ...EMPTY, businessOperatorId: operatorId })} searching={query.isFetching} columns={4}>
        <FormField label="事業実施者" required htmlFor="subsidy-operator">
          <Controller control={control} name="businessOperatorId" render={({ field }) => <OperatorSelect id="subsidy-operator" year={year} value={field.value} onChange={field.onChange} />} />
        </FormField>
        <FormField label="対象" span={3}>
          <Controller
            control={control}
            name="target"
            render={({ field }) => <RadioGroup name="target" value={field.value} onChange={field.onChange} options={SUBSIDY_TARGETS.map((t) => ({ value: t, label: t }))} />}
          />
        </FormField>
        <FormField label="船名">
          <TextField {...register('vesselName')} />
        </FormField>
        <FormField label="漁船登録番号">
          <TextField {...register('registrationNo')} />
        </FormField>
        <FormField label="出港日" span={2}>
          <span className="bs-exp-range">
            <DateField {...register('departureFrom')} />
            <span>〜</span>
            <DateField {...register('departureTo')} />
          </span>
        </FormField>
        <FormField label="調査日" span={2}>
          <span className="bs-exp-range">
            <DateField {...register('surveyFrom')} />
            <span>〜</span>
            <DateField {...register('surveyTo')} />
          </span>
        </FormField>
        <FormField label="助成金支払予定日" span={2}>
          <span className="bs-exp-range">
            <DateField {...register('scheduledFrom')} />
            <span>〜</span>
            <DateField {...register('scheduledTo')} />
          </span>
        </FormField>
        <FormField label="助成金支払日" span={2}>
          <span className="bs-exp-range">
            <DateField {...register('paidFrom')} />
            <span>〜</span>
            <DateField {...register('paidTo')} />
          </span>
        </FormField>
        <FormField label="助成金支払予定日の状況" span={2}>
          <Controller
            control={control}
            name="scheduled"
            render={({ field }) => <RadioGroup name="scheduled" value={field.value} onChange={field.onChange} options={(['予定済', '未予定', '両方'] as const).map((v) => ({ value: v, label: v }))} />}
          />
        </FormField>
        <FormField label="助成金支払日の状況" span={2}>
          <Controller
            control={control}
            name="paid"
            render={({ field }) => <RadioGroup name="paid" value={field.value} onChange={field.onChange} options={(['支払済', '未支払', '両方'] as const).map((v) => ({ value: v, label: v }))} />}
          />
        </FormField>
      </SearchPanel>

      <Panel title="一括入力" description={`対象：${filter.target}。チェックした項目のうち未入力の日付に設定日付を入れます。`}>
        <div className="bs-exp-bulk">
          <div className="bs-exp-bulk__group">
            <span className="bs-exp-bulk__label">項目</span>
            <span className="bs-exp-row">
              <Checkbox label="助成金支払予定日" checked={bulk.scheduled} onChange={(e) => setBulk((b) => ({ ...b, scheduled: e.target.checked }))} />
              {!isCoop && <Checkbox label="助成金支払日" checked={bulk.paid} onChange={(e) => setBulk((b) => ({ ...b, paid: e.target.checked }))} />}
            </span>
          </div>
          <div className="bs-exp-bulk__group">
            <label className="bs-exp-bulk__label" htmlFor="subsidy-bulk-date">
              日付設定
            </label>
            <DateField id="subsidy-bulk-date" value={bulk.date} onChange={(e) => setBulk((b) => ({ ...b, date: e.target.value }))} />
          </div>
          <span className="bs-exp-row">
            <Button icon={<CalendarCheck2 size={16} />} onClick={() => void applyBulk(false)} disabled={!rows.length}>
              支払日一括設定
            </Button>
            <Button variant="ghost" icon={<Eraser size={16} />} onClick={() => void applyBulk(true)} disabled={!rows.length}>
              支払日一括クリア
            </Button>
          </span>
        </div>
      </Panel>

      {operatorId == null ? (
        <EmptyState icon={<HandCoins />} title="事業実施者を選択して検索してください" />
      ) : query.error ? (
        <ErrorState error={query.error} onRetry={() => void query.refetch()} />
      ) : query.isPending ? (
        <Loading block />
      ) : rows.length === 0 ? (
        <EmptyState icon={<HandCoins />} title="該当するデータはありません" description={range(filter.surveyFrom, filter.surveyTo) ? '調査日の範囲を見直してください。' : '検索条件を変更してください。'} />
      ) : (
        <div className="bs-exp-stack">
          <SummaryStrip
            items={
              isWage
                ? [
                    { label: '件数（調査日）', value: formatNumber(rows.length) },
                    { label: '賃金 合計', value: <Money value={sum((r) => r.wageTotal)} /> },
                    { label: '用船料 合計', value: <Money value={sum((r) => r.charterTotal)} /> },
                    { label: '総額', value: <Money value={sum((r) => (r.wageTotal ?? 0) + (r.charterTotal ?? 0))} />, emphasis: true },
                  ]
                : [
                    { label: '件数（速報）', value: formatNumber(rows.length) },
                    { label: `${filter.target} 合計金額`, value: <Money value={sum((r) => r.amount)} />, emphasis: true },
                  ]
            }
          />
          <div className="bs-exp-grid-wrap">
            <table className="bs-exp-grid" aria-label="助成金支払対象一覧">
              <thead>
                <tr>
                  <th className="is-sticky" style={{ minWidth: 150 }}>
                    船名（漁船登録番号）
                  </th>
                  <th>船長名</th>
                  <th>調査期間</th>
                  <th>乗船人員</th>
                  <th>トン数</th>
                  {isWage ? (
                    <>
                      <th>調査日</th>
                      <th>調査時間計</th>
                      <th>賃金</th>
                      <th>用船料</th>
                    </>
                  ) : (
                    <>
                      <th>{filter.target === '現像代' ? '現像日' : '給油日'}</th>
                      <th>合計金額</th>
                    </>
                  )}
                  <th className="is-sep">助成金支払予定日</th>
                  <th>助成金支払日</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={r.id} className={cx(original.get(r.id) !== `${r.subsidyScheduledOn}|${r.subsidyPaidOn}` && 'is-dirty')}>
                    <td className="is-sticky">
                      <strong>{r.vesselName}</strong>
                      <span className="bs-exp-muted bs-num">（{r.registrationNo}）</span>
                    </td>
                    <td>{r.captainName}</td>
                    <td className="bs-num">
                      {r.periodFrom}〜{r.periodTo}
                    </td>
                    <td className="is-right bs-num">{r.crewCount}</td>
                    <td className="is-right">
                      <Num value={r.tonnage} />
                    </td>
                    {isWage ? (
                      <>
                        <td className="bs-num">{r.surveyDate}</td>
                        <td className="is-right">
                          <Num value={r.surveyHours} />
                        </td>
                        <td className="is-right">
                          <Money value={r.wageTotal} />
                        </td>
                        <td className="is-right">
                          <Money value={r.charterTotal} />
                        </td>
                      </>
                    ) : (
                      <>
                        <td className="bs-num">{r.refDate ?? ''}</td>
                        <td className="is-right">
                          <Money value={r.amount} />
                        </td>
                      </>
                    )}
                    <td className="is-sep">
                      <CellDate
                        label={`${r.vesselName} ${r.surveyDate ?? r.periodFrom} 助成金支払予定日`}
                        value={r.subsidyScheduledOn}
                        onChange={(v) => setRows((p) => p.map((x, j) => (j === i ? { ...x, subsidyScheduledOn: v } : x)))}
                      />
                    </td>
                    <td>
                      <CellDate
                        label={`${r.vesselName} ${r.surveyDate ?? r.periodFrom} 助成金支払日`}
                        value={r.subsidyPaidOn}
                        disabled={isCoop}
                        onChange={(v) => setRows((p) => p.map((x, j) => (j === i ? { ...x, subsidyPaidOn: v } : x)))}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );
}
